import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, Send, FileWarning, Building2, Banknote, Package, Scale, ChevronRight } from "lucide-react";

const INR = (n) => (n == null ? "—" : `INR ${Number(n).toLocaleString("en-IN")}`);

const EMPTY = {
  legal_name: "", gstin: "", pan: "", cin: "",
  incorporation_date: "", ownership_type: "pvt_ltd",
  directors: [{ name: "", din: "", pan: "" }],
  registered_address: "",
  financials: [
    { fy: "FY23-24", turnover_inr: 0, net_profit_inr: 0 },
    { fy: "FY24-25", turnover_inr: 0, net_profit_inr: 0 },
    { fy: "FY25-26", turnover_inr: 0, net_profit_inr: 0 },
  ],
  net_worth_inr: 0, existing_debt_inr: 0,
  bank_statement_doc_key: "", itr_doc_key: "", audited_financials_doc_key: "",
  years_in_trade: 0, peak_monthly_volume_mt: 0,
  existing_suppliers: "", historical_default: false, default_details: "",
  collateral_type: "property", collateral_value_inr: 0, collateral_notes: "",
  trade_references: "",
  requested_limit_inr: 10000000,
  purpose_of_credit: "",
  self_declaration_ack: false,
};

/**
 * CreditLimitApplication — strict, authoritative in-house credit-limit
 * intake form for buyers and sellers.  Displays the INR 80 crore ceiling
 * banner at all times, enforces server-authoritative validation, and lets
 * the applicant view the current pending application.
 */
export function CreditLimitApplication({ api }) {
  const [policy, setPolicy] = useState(null);
  const [existing, setExisting] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, m, meRes] = await Promise.all([
        api.get("/credit-limit/policy"),
        api.get("/credit-applications/mine"),
        api.get("/auth/me"),
      ]);
      setPolicy(p.data);
      // ─── SECURITY LOCKOUT ────────────────────────────────────────────
      // Server stamps every payload with `applicant_id` + `applicant_email`.
      // If they do not match the currently-authenticated user, the session
      // is treated as compromised and the user is logged out immediately.
      const meUser = meRes.data?.user || meRes.data || {};
      const meEmail = (meUser.email || "").toLowerCase();
      const meId = String(meUser.id || meUser._id || "");
      const respEmail = (m.data?.applicant_email || "").toLowerCase();
      const respId = String(m.data?.applicant_id || "");
      const mismatch =
        (respEmail && meEmail && respEmail !== meEmail) ||
        (respId && meId && respId !== meId) ||
        (m.data?.applications || []).some(
          (a) => a.user_email && a.user_email.toLowerCase() !== meEmail,
        );
      if (mismatch) {
        toast.error("Security lockout — session terminated. Please sign in again.");
        try { localStorage.removeItem("token"); } catch { /* noop */ }
        setTimeout(() => { window.location.href = "/login"; }, 800);
        return;
      }
      setExisting(m.data?.applications || []);
    } catch { toast.error("Could not load credit application state"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const patchFy = (i, k, v) => setForm((f) => {
    const fs = [...f.financials]; fs[i] = { ...fs[i], [k]: v }; return { ...f, financials: fs };
  });
  const addDirector = () => setForm((f) => ({ ...f, directors: [...f.directors, { name: "", din: "", pan: "" }] }));
  const patchDir = (i, k, v) => setForm((f) => { const d=[...f.directors]; d[i]={...d[i],[k]:v}; return {...f, directors: d}; });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/credit-applications", form);
      toast.success("Application filed with the Vinci Trade Desk. You'll receive a formal decision by email.");
      setForm(EMPTY); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.[0]?.msg || "Submission failed — please re-check fields"));
    } finally { setBusy(false); }
  };

  const pending = existing.find((a) => a.status === "pending");
  const approved = existing.find((a) => a.status === "approved");

  return (
    <div className="pt-2 max-w-4xl" data-testid="credit-limit-app">
      {/* Ceiling banner */}
      <div className="border-l-4 border-[#D8B23E] bg-gradient-to-r from-[#D8B23E]/10 to-transparent p-5 mb-8">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#D8B23E]"/>
          <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Vinci Working-Capital Programme</span>
        </div>
        <div className="mt-2 font-serif text-2xl md:text-3xl">Maximum Credit Limit — {policy?.max_display || "INR 80,00,00,000"}</div>
        <p className="font-body text-[13px] text-[#8A8A82] mt-2 leading-relaxed">
          {policy?.working_capital_notice || "The Vinci Working-Capital programme extends secured credit limits up to a ceiling of INR 80 crore, granted in-house by the Vinci Trade Desk following formal scrutiny."}
        </p>
      </div>

      {approved && (
        <div className="border border-[#2FB999]/40 rounded-sm p-4 mb-6 bg-[#2FB999]/10" data-testid="credit-approved-banner">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#2FB999]">Sanctioned Working-Capital Limit</div>
          <div className="font-serif text-2xl mt-1 text-[#F5F5F0]">{INR(approved.approved_limit_inr)}</div>
          <div className="font-mono text-[11px] text-[#8A8A82] mt-1">{approved.case_ref}</div>
        </div>
      )}
      {pending && (
        <div className="border border-[#D8B23E]/40 rounded-sm p-4 mb-6 bg-[#D8B23E]/5" data-testid="credit-pending-banner">
          <div className="flex items-center gap-2">
            <Loader2 size={14} className="animate-spin text-[#D8B23E]"/>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E]">Application Under Review</span>
          </div>
          <div className="font-mono text-[11px] text-[#8A8A82] mt-1">{pending.case_ref} · Requested {INR(pending.requested_limit_inr)}</div>
        </div>
      )}

      {!pending && (
        <div className="space-y-8">
          <Section icon={Building2} title="Business Identity">
            <Grid>
              <F label="Legal Business Name *"><input data-testid="cr-legal-name" value={form.legal_name} onChange={(e)=>patch("legal_name", e.target.value)} className={inputCls}/></F>
              <F label="Ownership Type *">
                <select data-testid="cr-ownership" value={form.ownership_type} onChange={(e)=>patch("ownership_type", e.target.value)} className={inputCls}>
                  {["pvt_ltd","public_ltd","llp","partnership","proprietorship","huf","other"].map((v)=><option key={v} value={v}>{v.replace("_"," ").toUpperCase()}</option>)}
                </select>
              </F>
              <F label="GSTIN *"><input data-testid="cr-gstin" value={form.gstin} onChange={(e)=>patch("gstin", e.target.value.toUpperCase())} placeholder="15-char GSTIN" maxLength={15} className={inputCls}/></F>
              <F label="PAN *"><input data-testid="cr-pan" value={form.pan} onChange={(e)=>patch("pan", e.target.value.toUpperCase())} placeholder="10-char PAN" maxLength={10} className={inputCls}/></F>
              <F label="CIN"><input data-testid="cr-cin" value={form.cin} onChange={(e)=>patch("cin", e.target.value.toUpperCase())} placeholder="U74999MH2020PTC000000" maxLength={21} className={inputCls}/></F>
              <F label="Incorporation Date *"><input data-testid="cr-incdate" type="date" value={form.incorporation_date} onChange={(e)=>patch("incorporation_date", e.target.value)} className={inputCls}/></F>
              <F label="Registered Address *" wide>
                <textarea data-testid="cr-address" value={form.registered_address} onChange={(e)=>patch("registered_address", e.target.value)} rows={2} className={`${inputCls} resize-none`}/>
              </F>
            </Grid>
            <div className="mt-3">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Directors / Partners</div>
              {form.directors.map((d, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                  <input data-testid={`cr-dir-name-${i}`} placeholder="Full name" value={d.name} onChange={(e)=>patchDir(i,"name",e.target.value)} className={inputCls}/>
                  <input data-testid={`cr-dir-din-${i}`} placeholder="DIN" value={d.din||""} onChange={(e)=>patchDir(i,"din",e.target.value)} className={inputCls}/>
                  <input data-testid={`cr-dir-pan-${i}`} placeholder="PAN" value={d.pan||""} onChange={(e)=>patchDir(i,"pan",e.target.value.toUpperCase())} className={inputCls}/>
                </div>
              ))}
              <button onClick={addDirector} className="text-[#B87333] hover:text-[#D8B23E] font-grotesk text-[10px] uppercase tracking-[0.22em]">+ Add director</button>
            </div>
          </Section>

          <Section icon={Banknote} title="Financial Position (Last 3 FYs)">
            <Grid>
              {form.financials.map((r, i) => (
                <div key={i} className="col-span-full grid grid-cols-3 gap-2 items-end">
                  <F label={`FY ${i+1} label`}><input data-testid={`cr-fy-${i}-name`} value={r.fy} onChange={(e)=>patchFy(i,"fy",e.target.value)} className={inputCls}/></F>
                  <F label="Turnover (INR)"><input data-testid={`cr-fy-${i}-turnover`} type="number" value={r.turnover_inr} onChange={(e)=>patchFy(i,"turnover_inr",Number(e.target.value))} className={inputCls}/></F>
                  <F label="Net Profit (INR)"><input data-testid={`cr-fy-${i}-profit`} type="number" value={r.net_profit_inr} onChange={(e)=>patchFy(i,"net_profit_inr",Number(e.target.value))} className={inputCls}/></F>
                </div>
              ))}
              <F label="Current Net Worth (INR) *"><input data-testid="cr-networth" type="number" value={form.net_worth_inr} onChange={(e)=>patch("net_worth_inr",Number(e.target.value))} className={inputCls}/></F>
              <F label="Existing Debt (INR) *"><input data-testid="cr-debt" type="number" value={form.existing_debt_inr} onChange={(e)=>patch("existing_debt_inr",Number(e.target.value))} className={inputCls}/></F>
            </Grid>
          </Section>

          <Section icon={Package} title="Trade History">
            <Grid>
              <F label="Years in Trade *"><input data-testid="cr-years" type="number" min={0} value={form.years_in_trade} onChange={(e)=>patch("years_in_trade",Number(e.target.value))} className={inputCls}/></F>
              <F label="Peak Monthly Volume (MT) *"><input data-testid="cr-peak" type="number" value={form.peak_monthly_volume_mt} onChange={(e)=>patch("peak_monthly_volume_mt",Number(e.target.value))} className={inputCls}/></F>
              <F label="Historical Default?"><select data-testid="cr-default" value={form.historical_default?"yes":"no"} onChange={(e)=>patch("historical_default", e.target.value === "yes")} className={inputCls}><option value="no">No</option><option value="yes">Yes</option></select></F>
              <F label="Existing Suppliers (freeform)" wide><textarea data-testid="cr-suppliers" rows={2} value={form.existing_suppliers} onChange={(e)=>patch("existing_suppliers", e.target.value)} className={`${inputCls} resize-none`}/></F>
              {form.historical_default && (
                <F label="Default Details" wide><textarea data-testid="cr-default-details" rows={2} value={form.default_details||""} onChange={(e)=>patch("default_details", e.target.value)} className={`${inputCls} resize-none`}/></F>
              )}
            </Grid>
          </Section>

          <Section icon={Scale} title="Collateral & Security">
            <Grid>
              <F label="Collateral Type *">
                <select data-testid="cr-coll-type" value={form.collateral_type} onChange={(e)=>patch("collateral_type", e.target.value)} className={inputCls}>
                  {["property","fdr","corporate_guarantee","personal_guarantee","none"].map((v)=><option key={v} value={v}>{v.replace("_"," ").toUpperCase()}</option>)}
                </select>
              </F>
              <F label="Collateral Value (INR) *"><input data-testid="cr-coll-val" type="number" value={form.collateral_value_inr} onChange={(e)=>patch("collateral_value_inr",Number(e.target.value))} className={inputCls}/></F>
              <F label="Collateral Notes" wide><textarea data-testid="cr-coll-notes" rows={2} value={form.collateral_notes||""} onChange={(e)=>patch("collateral_notes", e.target.value)} className={`${inputCls} resize-none`}/></F>
              <F label="Trade References" wide><textarea data-testid="cr-refs" rows={2} value={form.trade_references} onChange={(e)=>patch("trade_references", e.target.value)} className={`${inputCls} resize-none`} placeholder="Names + contacts of two existing counterparties."/></F>
            </Grid>
          </Section>

          <Section icon={ShieldCheck} title="Requested Limit & Purpose">
            <Grid>
              <F label={`Requested Limit (max ${policy?.max_display || "INR 80 Cr"}) *`} wide>
                <input data-testid="cr-req" type="number" min={100000} max={800000000}
                       value={form.requested_limit_inr}
                       onChange={(e)=>patch("requested_limit_inr", Math.min(800000000, Number(e.target.value)))}
                       className={inputCls}/>
                <div className="font-body text-[11px] text-[#8A8A82] mt-1">{INR(form.requested_limit_inr)}</div>
              </F>
              <F label="Purpose of Credit *" wide>
                <textarea data-testid="cr-purpose" rows={3} value={form.purpose_of_credit} onChange={(e)=>patch("purpose_of_credit", e.target.value)} className={`${inputCls} resize-none`} placeholder="Concrete, verifiable purpose (min 20 characters)."/>
              </F>
            </Grid>
            <label className="mt-4 flex items-start gap-2 cursor-pointer" data-testid="cr-ack-label">
              <input data-testid="cr-ack" type="checkbox" checked={form.self_declaration_ack} onChange={(e)=>patch("self_declaration_ack", e.target.checked)} className="mt-1"/>
              <span className="font-body text-[12px] text-[#C9C9C2]">
                I hereby declare that the information provided is true and correct to the best of my knowledge. I authorise the Vinci Trade Desk to make lawful enquiries and to seek references as necessary for the purpose of this application.
              </span>
            </label>
          </Section>

          <div className="flex justify-end">
            <button
              data-testid="cr-submit"
              disabled={busy || !form.self_declaration_ack}
              onClick={submit}
              className="inline-flex items-center gap-2 rounded-sm bg-gradient-to-r from-[#B87333] to-[#D8B23E] text-black px-6 py-3 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40"
            >
              {busy ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>} Submit for Scrutiny <ChevronRight size={13}/>
            </button>
          </div>
        </div>
      )}

      {existing.length > 0 && (
        <div className="mt-12">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82] mb-3">History</div>
          <div className="space-y-2">
            {existing.map((a) => (
              <div key={a.id} className="p-3 border border-white/10 rounded-sm flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-mono text-[11px] text-[#D8B23E]">{a.case_ref}</div>
                  <div className="font-body text-[13px]">{INR(a.requested_limit_inr)} · {new Date(a.submitted_at).toLocaleDateString("en-IN")}</div>
                </div>
                <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border" style={{
                  color: a.status === "approved" ? "#2FB999" : a.status === "rejected" ? "#C0392B" : "#D8B23E",
                  borderColor: (a.status === "approved" ? "#2FB999" : a.status === "rejected" ? "#C0392B" : "#D8B23E") + "55",
                }}>{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]";
const F = ({ label, wide, children }) => (
  <label className={`block ${wide ? "col-span-full" : ""}`}>
    <div className="font-grotesk text-[9px] tracking-[0.22em] text-[#8A8A82] uppercase mb-1">{label}</div>
    {children}
  </label>
);
const Grid = ({ children }) => <div className="grid md:grid-cols-2 gap-3">{children}</div>;
const Section = ({ icon: Icon, title, children }) => (
  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 rounded-sm p-5 bg-black/30">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={14} className="text-[#D8B23E]"/>
      <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">{title}</span>
    </div>
    {children}
  </motion.div>
);
